import { useCallback, useState } from 'react'
import { finishOfferedByStatus, offersPurchase, useProStatus } from '../../api/proStatus'
import { useSupabaseToken } from '../../hooks/useSupabaseToken'
import { trackEvent } from '../../lib/analytics'
import { purchaseSurface } from '../../lib/revenuecat'
import Paywall from '../pro/Paywall'
import { goProLabel, homeBandAria, homeBandSubtitle, homeBandTitle } from '../pro/proCopy'

/**
 * Pro on the home hub. The app's THIRD route to the paywall and the first one
 * on a surface people already visit.
 *
 * Before this there were two, both of which had to be hunted for: /account ->
 * Subscription -> Go Pro, and the quota pill inside Deck Chat, which costs an
 * analyzed deck and a sent message to reach.
 *
 * RETURNING USERS ONLY - HomePage renders it inside the returning hub bands.
 * What Pro sells is unlimited Deck Chat, and somebody with no saved deck has
 * nothing to chat about; advertising it before the first build sells a feature
 * they cannot use yet and displaces the one action that matters.
 *
 * Renders NOTHING until /pro/status has answered, the same rule
 * SubscriptionCard and ChatQuotaPill follow: `undefined` is "not read yet",
 * not "free", and `useProStatus` is already disabled without a resolved
 * session.
 */
export default function ProBand() {
  const { userId, token } = useSupabaseToken()
  const { data: status } = useProStatus()
  const [paywallOpen, setPaywallOpen] = useState(false)

  const closePaywall = useCallback(() => setPaywallOpen(false), [])
  const openPaywall = useCallback(() => {
    trackEvent('pro_paywall_opened', { trigger: 'home_band' })
    setPaywallOpen(true)
  }, [])

  // Nothing to sell inside an Android app build without Play billing
  // (purchaseSurface, src/lib/revenuecat.ts).
  if (!offersPurchase(status) || purchaseSurface() === 'none') return null

  return (
    <div data-testid="home-pro-band">
      <button
        type="button"
        onClick={openPaywall}
        aria-label={homeBandAria()}
        className="w-full text-left rounded-xl border border-purple-500/35 bg-purple-600/10 p-4 hover:bg-purple-600/20 transition-colors"
      >
        <p className="text-sm font-semibold text-gray-100">{homeBandTitle()}</p>
        <p className="mt-1 text-sm text-gray-400">{homeBandSubtitle()}</p>
        <p className="mt-2 text-sm font-semibold text-purple-300">{goProLabel()}</p>
      </button>
      <Paywall
        proMonthlyMessages={status?.pro_cap_month}
        finishOffered={finishOfferedByStatus(status)}
        open={paywallOpen}
        onClose={closePaywall}
        userId={userId}
        token={token}
        isPro={false}
        // eslint-disable-next-line i18next/no-literal-string -- entry code
        entry="home_band"
      />
    </div>
  )
}
